/* eslint-disable @typescript-eslint/no-explicit-any*/

/**
 * Returns `pass` if prop is truthy. Otherwise returns `fail`
 *
 * @example
 * const Button = styled.button`
 *   color: ${ifProp("transparent", "transparent", "black")};
 * `;
 *
 * <Button /> // color = "black";
 * <Button transparent /> // color = "transparent";
 */
function ifProp(name, pass = "", fail = "") {
  return props => {
    const isPass = Boolean(props[name]);
    const result = isPass ? pass : fail;
    return typeof result === "function" ? result(props) : result;
  };
}

/**
 * Returns `pass` if prop is not truthy. Otherwise returns `fail`
 *
 * @example
 * const Button = styled.button`
 *   color: ${ifNotProp("transparent", "transparent", "black")};
 * `;
 *
 * <Button /> // color = "transparent";
 * <Button transparent /> // color = "black";
 */

const ifNotProp = (name, pass = "", fail = "") => ifProp(name, fail, pass);

/* eslint-disable @typescript-eslint/no-explicit-any*/

/**
 * Switches on a given prop. Returns the value or function for a given prop value. Third parameter is default value.
 *
 * @example
 * const Button = styled.button`
 *   font-size: ${switchProp(prop("size", "medium"), {
 *     small: font("sm"),
 *     medium: font("md"),
 *     large: font("lg")
 *   }};
 *   ${switchProp("type", {
 *     light: css`
 *       color: white;
 *     `,
 *     dark: css`
 *       color: black;
 *     `
 *   }, css`color: yellow;`)}
 * `;
 *
 * <Button size="large" theme={{ kind: "light" }} />
 */
function switchProp(needle, cases, defaultCase) {
  return props => {
    const value = typeof needle === "function" ? needle(props) : props[needle];

    if (value in cases) {
      return cases[value];
    }

    return defaultCase;
  };
}

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Calls a function passing properties values as arguments.
 *
 * @example *
 * const Button = styled.button`
 *   font-size: ${withProp("size", size => `${size}rem`)};
 *   border-color: ${withProp(color("primary", "blue"), darken(0.5))};
 * `;
 */
function withProp(needle, fn) {
  return props => {
    if (typeof needle === "function") {
      return fn(needle(props));
    }

    return fn(props[needle]);
  };
}

/* eslint-disable @typescript-eslint/no-explicit-any*/

/**
 * Returns the value of `props[name]` or `defaultValue`
 *
 * @param name - prop name
 * @param [defaultValue] - default prop value
 * @example
 * const Button = styled.button<{ color?: string }>`
 *   color: ${prop("color", "red")};
 * `;
 *
 * <Button /> // color = "red"
 * <Button color="blue" /> // color = "blue"
 */
function prop(name, defaultValue) {
  return props => {
    if (typeof props[name] !== "undefined") {
      return props[name];
    }

    return defaultValue;
  };
}

export { ifNotProp, ifProp, prop, switchProp, withProp };
